import { createAsyncThunk } from "@reduxjs/toolkit";
import modelService from "../../../api/services/model";
import { COLLECTION_GROUP_TYPE } from "../../../utils/constants";

export const getCategories = createAsyncThunk(
  "model/get-categories",
  async () => {
    return await modelService.getCategories();
  },
);

export const getCollections = createAsyncThunk(
  "model/get-collections",
  async ({ page, search, filter }) => {
    return await modelService.getCollections({ page, search, filter });
  },
);

export const getExploreCollections = createAsyncThunk(
  "model/get-explore-collections",
  async ({ page, search, categoryId, keyword }) => {
    return await modelService.getExploreCollections({
      page,
      search,
      categoryId,
      keyword,
    });
  },
);

export const getUserFavorites = createAsyncThunk(
  "model/get-user-favorites",
  async ({ page, search, keyword }) => {
    return await modelService.getUserFavorites({
      page,
      search,
      keyword,
    });
  },
);

export const generateCollection = createAsyncThunk(
  "model/generate-collection",
  async (requestData, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      if (requestData.image) {
        formData.append(
          "image",
          requestData.image.file,
          requestData.image.file.name,
        );
      }

      if (requestData.video) {
        formData.append("video", requestData.video.file);
      }

      formData.append("modelId", requestData.modelId);

      if (requestData.customPrompt && requestData.customPrompt.trim() !== "") {
        formData.append("customPrompt", requestData.customPrompt);
      }

      if (
        requestData.negativePrompt &&
        requestData.negativePrompt.trim() !== ""
      ) {
        formData.append("negativePrompt", requestData.negativePrompt);
      }

      if (requestData.nudify) {
        formData.append("nudify", requestData.nudify);
      }

      if (requestData.upscale) {
        formData.append("upscale", requestData.upscale);
      }

      if (requestData.resolution) {
        formData.append("resolution", requestData.resolution);
      }

      if (requestData.seed) {
        formData.append("seed", requestData.seed);
      }

      if (requestData.fps) {
        formData.append("fps", requestData.fps);
      }

      if (requestData.videoDuration) {
        formData.append("videoDuration", requestData.videoDuration);
      }

      if (requestData.extendModelId) {
        formData.append("extendModelId", requestData.extendModelId);
      }

      return await modelService.generateCollection(formData);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  },
);

export const enchantPrompt = createAsyncThunk(
  "model/enchant-prompt",
  async ({ prompt }, { rejectWithValue }) => {
    try {
      return await modelService.enchantPrompt({ prompt });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to enchant prompt");
    }
  },
);

export const generateImageCollection = createAsyncThunk(
  "model/generate-image-collection",
  async (requestData, { rejectWithValue }) => {
    try {
      return await modelService.generateImageCollection(requestData);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  },
);

export const regenerateCollection = createAsyncThunk(
  "model/regenerate-collection",
  async ({ collectionId, seed }, { rejectWithValue }) => {
    try {
      return await modelService.regenerateCollection({ collectionId, seed });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  },
);

export const regenerateImageCollection = createAsyncThunk(
  "model/regenerate-text-on-body-collection",
  async ({ collectionId, seed }, { rejectWithValue }) => {
    try {
      return await modelService.regenerateImageCollection({
        collectionId,
        seed,
      });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  },
);

export const generateAnonymize = createAsyncThunk(
  "model/generate-anonymize-collection",
  async (requestData, { rejectWithValue }) => {
    try {
      return await modelService.generateAnonymize(requestData);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  },
);

export const toggleToFavorite = createAsyncThunk(
  "model/toggle-to-favorite",
  async ({ collectionId }) => {
    return await modelService.toggleToFavorite({
      collectionId,
    });
  },
);

export const deleteCollection = createAsyncThunk(
  "model/delete-collection",
  async (collectionId) => {
    return await modelService.deleteCollection(collectionId);
  },
);

export const hideCollection = createAsyncThunk(
  "model/hide-collection",
  async ({ collectionId, hide }, { rejectWithValue }) => {
    try {
      return await modelService.hideCollection({ collectionId, hide });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to hide collection");
    }
  },
);

export const hideCollectionVip = createAsyncThunk(
  "model/hide-collection-vip",
  async ({ collectionId, hide }, { rejectWithValue }) => {
    try {
      return await modelService.hideCollectionVip({ collectionId, hide });
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to hide collection from VIP",
      );
    }
  },
);

export const rateModel = createAsyncThunk("model/rate-model", async (data) => {
  return await modelService.rateModel(data);
});

export const getTrainModels = createAsyncThunk(
  "model/get-train-models",
  async (requestData) => {
    return await modelService.getTrainModels(requestData);
  },
);

export const getTrainModel = createAsyncThunk(
  "model/get-train-model",
  async (trainModelId) => {
    return await modelService.getTrainModel(trainModelId);
  },
);

export const fetchCollection = createAsyncThunk(
  "model/get-collection-details",
  async (collectionId) => {
    return await modelService.fetchCollection(collectionId);
  },
);

export const createTrainModel = createAsyncThunk(
  "model/create-train-model",
  async ({ name, type }, { rejectWithValue }) => {
    try {
      return await modelService.createTrainModel({ name, type });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create train model");
    }
  },
);

export const updateTrainModel = createAsyncThunk(
  "model/update-train-model",
  async (
    {
      trainModelId,
      name,
      videosUrl,
      imagesUrl,
      prompt,
      inputControlImagesUrl,
      inputImagesUrl,
      videoLength,
    },
    { rejectWithValue },
  ) => {
    try {
      return await modelService.updateTrainModel({
        formData: {
          trainModelId,
          name,
          videosUrl,
          imagesUrl,
          prompt,
          inputControlImagesUrl,
          inputImagesUrl,
          videoLength,
        },
        trainModelId,
      });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update train model");
    }
  },
);

export const deleteTrainModel = createAsyncThunk(
  "model/delete-train-model",
  async (trainModelId) => {
    return await modelService.deleteTrainModel(trainModelId);
  },
);

export const fetchTopCreators = createAsyncThunk(
  "model/fetch-top-creators",
  async () => {
    return await modelService.getTopCreators();
  },
);

export const fetchDefaultGallery = createAsyncThunk(
  "model/fetch-gallery",
  async () => {
    return await modelService.getDefaultGallery();
  },
);

export const fetchAnonymizedCollections = createAsyncThunk(
  "model/fetch-anonymized-collections",
  async () => {
    return await modelService.getGroupCollections(
      COLLECTION_GROUP_TYPE.ANONYMIZE,
    );
  },
);

export const selectCollectionsInCollectionGroup = createAsyncThunk(
  "model/select-collections-group",
  async (requestData) => {
    return await modelService.selectCollectionsInGroupCollections(requestData);
  },
);

export const selectCoverForTrainModel = createAsyncThunk(
  "model/select-cover",
  async ({ trainModelId, collectionId, templateName }) => {
    return await modelService.selectCoverForTrainModel({
      trainModelId,
      collectionId,
      templateName,
    });
  },
);

export const setCollectionPublicCover = createAsyncThunk(
  "model/set-public-cover",
  async ({ collectionId }) => {
    return await modelService.setCollectionPublicCover({ collectionId });
  },
);

export const submitTrainModelForReview = createAsyncThunk(
  "model/submit-review",
  async (requestData) => {
    return await modelService.submitTrainModelForReview(requestData);
  },
);

export const getFolders = createAsyncThunk(
  "model/get-collection-folders",
  async () => {
    return await modelService.getFolders();
  },
);

export const createFolder = createAsyncThunk(
  "model/create-collection-folders",
  async (requestData) => {
    return await modelService.createFolder(requestData);
  },
);

export const deleteFolder = createAsyncThunk(
  "model/delete-collection-folders",
  async (requestData) => {
    return await modelService.deleteFolder(requestData);
  },
);

export const putCollectionToFolder = createAsyncThunk(
  "model/put-collection-to-folder",
  async (requestData) => {
    return await modelService.putCollectionToFolder(requestData);
  },
);

export const editFolder = createAsyncThunk(
  "model/edit-collection-folder",
  async (requestData) => {
    return await modelService.editFolder(requestData);
  },
);
